import {
  CreatePartnerType,
  UpdatePartnerType,
} from "types/partners/PartnerCommandTypes";
import {
  PartnerListFiltersType,
  PartnerListType,
} from "types/partners/PartnerTypes";
import { FetchError } from "exceptions/exceptions";
import { API_URL } from "./common";

const ENDPOINT = `${API_URL}/v1/ecommerce/partners`;

/**
 * Partner Overview list (EE-272). `page` is 1-based, matching the API;
 * `pageSize` is capped at 300 server-side. Only non-empty params are sent, so
 * a blank search or an "All" dropdown is the absence of a filter rather than
 * `search=` on the wire.
 */
export const apiGetPartners = (
  token: string,
  filters: PartnerListFiltersType = {}
): Promise<PartnerListType> => {
  const {
    search,
    templateType,
    templateDelivery,
    createdFrom,
    createdTo,
    page = 1,
    // The backend caps pageSize at 300; that ceiling is not a sane default -
    // a caller that omits it wants a page, not the whole table.
    pageSize = 25,
    sortKey = "id",
    sortDirection = "desc",
  } = filters;

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  params.set("sortKey", sortKey);
  params.set("sortDirection", sortDirection);
  if (search) params.set("search", search);
  if (templateType) params.set("templateType", templateType);
  if (templateDelivery) params.set("templateDelivery", templateDelivery);
  if (createdFrom) params.set("createdFrom", createdFrom);
  if (createdTo) params.set("createdTo", createdTo);

  return fetch(`${ENDPOINT}?${params.toString()}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json();
    else throw new Error(res.statusText);
  });
};

export const apiGetPartner = (token: string, id: number) => {
  return fetch(`${ENDPOINT}/${id}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json();
    else throw new Error(res.statusText);
  });
};

export const apiCreatePartner = (token: string, command: CreatePartnerType) => {
  return fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(command),
  }).then(async (res) => {
    // The body says what was wrong: the 422 lists each refused field.
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });
};

export const apiUpdatePartner = (
  token: string,
  id: number,
  command: UpdatePartnerType
) => {
  return fetch(`${ENDPOINT}/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(command),
  }).then(async (res) => {
    // The body says what was wrong: the 422 lists each refused field.
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });
};

export const apiDeletePartner = (token: string, id: number) => {
  return fetch(`${ENDPOINT}/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.text();
    else throw new Error(res.statusText);
  });
};
